/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
/* eslint-disable max-classes-per-file */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *           _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import {
  Component, EventEmitter, Input, Output, OnInit, Inject,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

/**
 * Component for Audio playback after recording.
 */
@Component({
  selector: 'audio-playback',
  templateUrl: './audio-playback.component.html',
  standalone: false,
})
export class AudioPlaybackComponent implements OnInit {
  /**
   * Object created URL for the recorded audio.
   */
  @Input() audioSrc: SafeUrl;

  /**
   * Boolean indicating whether or not the user has exceeded the recording limit.
   */
  @Input() exceededRecordingLimit : boolean;

  /**
   * Value for the difference in audio recording time since started.
   */
  @Input() audioRecordingTimeDifference : number;

  /**
   * Event Emitter for communicating with the parent audio recording component for deleting the audio recording.
   */
  @Output() deleteAudioRecordingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event Emitter for communicating with the parent audio recording component for saving the audio recording.
   */
  @Output() saveAudioRecordingEmitter: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event Emitter for communicating with the parent audio recording component for downloading the audio recording.
   */
  @Output() downloadAudioRecordingEmitter: EventEmitter<string> = new EventEmitter<string>();

  /**
   * File name for the recorded audio that the user sets.
   */
  public fileName: string;

  /**
   * Whether or not the user is on Mobile.
   */
  public isMobile : boolean;

  /**
   * Default Constructor.
   * @param BulbBrowserService
   */
  constructor(@Inject('BulbBrowserService') private BulbBrowserService) {}

  ngOnInit() {
    // Initialize / register the custom Audio Player:
    this.isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    setTimeout((e) => {
      new AudioPlaybackComponent.AudioPlayer(this.isMobile);
    });
  }

  deleteAudioRecording(): void {
    this.deleteAudioRecordingEmitter.emit(true);
  }

  saveRecording(): void {
    const fileName = (this.fileName) ? this._cleanFileName() : this.generateFileName();
    this.saveAudioRecordingEmitter.emit({ save: true, fileName });
  }

  downloadRecordedAudio(): void {
    (this.fileName) ? this.downloadAudioRecordingEmitter.emit(this._cleanFileName())
      : this.downloadAudioRecordingEmitter.emit(this.generateFileName());
  }

  /**
   * Private helper method to clean the user generated audio recording file name.
   * @private
   */
  _cleanFileName(): string {
    this.fileName = `${this.fileName.replace(/\s/g, '_')}.wav`;
    return this.fileName;
  }

  /**
   * Method to generate a temporary file name.
   */
  generateFileName(fileName?: string): string {
    return `${(fileName || '') + new Date().toISOString()}.wav`;
  }

  /**
   * Inner Class to handle displaying the audio player after audio recording.
   */
  static AudioPlayer = class {
    /**
     * Default constructor.
     *
     * @param isMobile Boolean, indicating if the user is on a mobile device. We need to wait half a second due to
     * how the elements are rendered on a page via the mobile web engines.
     */
    constructor(isMobile) {
      setTimeout(() => {
        this._initAudioPlayer();
      }, (isMobile) ? 500 : 0);
    }

    _initAudioPlayer(): void {
      const $playButton = (<any>jQuery('.bulb-playback-audio-play'));
      const $progressBar = (<any>jQuery('.bulb-playback-audio-progressbar'));
      const $time = jQuery('.time');
      const $startTime = jQuery('.bulb-playback-audio-start-time');
      const $muteButton = jQuery('.bulb-playback-audio-mute');
      const $volumeBar = (<any>jQuery('.bulb-playback-audio-volume'));
      const $player = (<HTMLAudioElement>document.getElementById('audio-recording-playback-player'));
      let duration: number;
      let volume: number = 0.75;

      $player.load();
      $player.volume = volume;

      $player.onloadedmetadata = function () {
        duration = $player.duration;
        $progressBar.progressbar('option', { max: duration });
      };

      $player.oncanplay = (event) => {
        $time.text(getTime($player.duration));
      };

      $player.addEventListener('timeupdate', () => {
        $progressBar.progressbar('value', $player.currentTime);
        $startTime.text(getTime($player.currentTime));
      }, false);

      /**
       * Helper method to return the current time of the player in a readable format.
       * @param t Time
       */
      function getTime(t) {
        const m = ~~(t / 60); const
          s = ~~(t % 60);
        return `${m < 10 ? `0${m}` : m}:${s < 10 ? `0${s}` : s}`;
      }

      function getProgressBarClickInfo(progress_bar, e) {
        // Retrieve the current position of an element *relative* to the document:
        const offset = progress_bar.offset();
        const x = (e.pageX - offset.left);
        const y = (e.pageY - offset.top);
        const max = progress_bar.progressbar('option', 'max');
        const value = x * max / progress_bar.width();

        return {
          x,
          y,
          max,
          value,
        };
      }

      $volumeBar.progressbar({
        value: $player.volume * 100,
      });

      $volumeBar.click(function (e) {
        const info = getProgressBarClickInfo($(this), e);
        $volumeBar.progressbar('value', info.value);
        $player.volume = info.value / info.max;
      });

      $progressBar.progressbar({
        value: $player.currentTime,
      });

      $progressBar.click(function (e) {
        const info = getProgressBarClickInfo($(this), e);
        $player.currentTime = ((info.max * info.value) / $player.duration);
      });

      $playButton.click(function () {
        const changeState = $player.paused;
        $player[changeState ? 'play' : 'pause']();
        jQuery(this).toggleClass('fa-play', !changeState);
        jQuery(this).toggleClass('fa-pause', changeState);
      });

      $muteButton.click(function () {
        if ($player.volume === 0) {
          $player.volume = volume;
        } else {
          volume = $player.volume;
          $player.volume = 0;
        }
        $volumeBar.progressbar('value', $player.volume * 100);
        jQuery(this).toggleClass('fa-volume-up', $player.volume !== 0);
        jQuery(this).toggleClass('fa-volume-off', $player.volume === 0);
      });
    }
  };
}
